import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fileToBase64 } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Upload, Eye, Download, Trash2, UserCog } from "lucide-react";

const UPPER = (e) => (e?.target?.value ?? "").toUpperCase();

function viewBase64(b64) {
    if (!b64) return;
    const win = window.open();
    if (win) {
        win.document.write(
            `<title>Preview</title><body style="margin:0;display:grid;place-items:center;background:#0f172a"><img src="${b64}" style="max-width:100vw;max-height:100vh"/></body>`
        );
    }
}

function downloadBase64(b64, fname) {
    if (!b64) return;
    const a = document.createElement("a");
    a.href = b64;
    a.download = fname || "document";
    a.click();
}

function FileField({ label, value, onChange, testid, required }) {
    return (
        <div className="space-y-1.5">
            <Label>
                {label}
                {required && <span className="text-rose-600 ml-1">*</span>}
                {!required && (
                    <span className="text-xs font-normal text-slate-400 ml-1">(optional)</span>
                )}
            </Label>
            <div className="flex items-center gap-3 flex-wrap">
                <label
                    className="cursor-pointer inline-flex items-center gap-2 px-3 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                    data-testid={testid}
                >
                    <Upload className="h-4 w-4" />
                    {value ? "Change" : "Choose file"}
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const b64 = await fileToBase64(f);
                            onChange(b64);
                        }}
                    />
                </label>
                {value && (
                    <>
                        <img
                            src={value}
                            alt="preview"
                            className="h-11 w-11 rounded-lg object-cover border border-slate-200"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-lg"
                            data-testid={`${testid}-view`}
                            onClick={() => viewBase64(value)}
                        >
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-lg"
                            data-testid={`${testid}-download`}
                            onClick={() => downloadBase64(value, `${testid}.png`)}
                        >
                            <Download className="h-3.5 w-3.5 mr-1" /> Download
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-lg text-rose-700 border-rose-200 hover:bg-rose-50"
                            data-testid={`${testid}-delete`}
                            onClick={() => onChange("")}
                        >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function OtherDocsField({ value, onChange, testid }) {
    return (
        <div className="space-y-2 sm:col-span-2">
            <Label>
                Other Documents
                <span className="text-xs font-normal text-slate-400 ml-1">(optional)</span>
            </Label>
            <div className="flex items-center gap-3 flex-wrap">
                <label
                    data-testid={testid}
                    className="cursor-pointer inline-flex items-center gap-2 px-3 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                >
                    <Upload className="h-4 w-4" />
                    Add document
                    <input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        className="hidden"
                        onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            const arr = await Promise.all(files.map(fileToBase64));
                            onChange([...(value || []), ...arr]);
                            e.target.value = "";
                        }}
                    />
                </label>
            </div>
            {value && value.length > 0 && (
                <div className="grid sm:grid-cols-3 gap-2">
                    {value.map((b64, i) => (
                        <div
                            key={`${i}-${b64.slice(-12)}`}
                            className="flex items-center gap-2 bg-slate-50 rounded-xl p-2 border border-slate-100"
                        >
                            <img
                                src={b64}
                                alt={`doc-${i + 1}`}
                                className="h-12 w-12 rounded-lg object-cover border border-slate-200"
                            />
                            <div className="text-xs text-slate-600 flex-1 truncate">
                                Doc {i + 1}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                                data-testid={`other-doc-view-${i}`}
                                onClick={() => viewBase64(b64)}
                            >
                                <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                                data-testid={`other-doc-download-${i}`}
                                onClick={() => downloadBase64(b64, `document-${i + 1}.png`)}
                            >
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-rose-700"
                                data-testid={`other-doc-delete-${i}`}
                                onClick={() =>
                                    onChange(value.filter((_, idx) => idx !== i))
                                }
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


export default function CustomerForm() {
    const { t } = useLang();
    const nav = useNavigate();
    const { id: editId } = useParams();
    const isEdit = !!editId;
    const [busy, setBusy] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        first_name: "",
        middle_name: "",
        surname: "",
        mobile: "",
        alt_mobile: "",
        address: "",
        occupation: "",
        monthly_income: "",
        photo: "",
        aadhaar: "",
        pan: "",
        dob: "",
        other_docs: [],
        witness1: { full_name: "", address: "" },
        witness2: { full_name: "", address: "" },
        assigned_to: "",
    });

    // Load active employees in admin's branch scope for the assignment picker.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/users");
                setEmployees(
                    (data || []).filter((u) => u.role === "employee" && !u.disabled),
                );
            } catch {
                /* non-fatal: admin without users API access still gets a blank picker */
            }
        })();
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/customers/${editId}`);
                setForm({
                    first_name: data.first_name || "",
                    middle_name: data.middle_name || "",
                    surname: data.surname || "",
                    mobile: data.mobile || "",
                    alt_mobile: data.alt_mobile || "",
                    address: data.address || "",
                    occupation: data.occupation || "",
                    monthly_income: data.monthly_income || "",
                    photo: data.photo || "",
                    aadhaar: data.aadhaar || "",
                    pan: data.pan || "",
                    dob: data.dob || "",
                    other_docs: data.other_docs || [],
                    witness1: data.witness1 || { full_name: "", address: "" },
                    witness2: data.witness2 || { full_name: "", address: "" },
                    assigned_to: (data.assigned_to || "").toLowerCase(),
                });
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [editId, isEdit]);

    function set(k, v) {
        setForm((s) => ({ ...s, [k]: v }));
    }
    function setW(w, k, v) {
        setForm((s) => ({ ...s, [w]: { ...s[w], [k]: v } }));
    }

    function validate() {
        if (!form.first_name.trim()) return "First name is required";
        if (!form.surname.trim()) return "Surname is required";
        if (!form.mobile.trim()) return "Mobile number is required";
        if (!form.address.trim()) return "Address is required";
        if (!form.monthly_income || Number(form.monthly_income) <= 0)
            return "Monthly income is required";
        // KYC documents (photo, aadhaar, pan, other docs) are OPTIONAL.
        // Witnesses are optional. If a witness section is partially filled,
        // require both fields together.
        if ((form.witness1.full_name.trim() || form.witness1.address.trim()) &&
            (!form.witness1.full_name.trim() || !form.witness1.address.trim())) {
            return "Witness 1: please fill both name & address (or leave both empty)";
        }
        if ((form.witness2.full_name.trim() || form.witness2.address.trim()) &&
            (!form.witness2.full_name.trim() || !form.witness2.address.trim())) {
            return "Witness 2: please fill both name & address (or leave both empty)";
        }
        return null;
    }

    async function submit(e) {
        e.preventDefault();
        const err = validate();
        if (err) return toast.error(err);
        setBusy(true);
        try {
            const payload = {
                ...form,
                monthly_income: Number(form.monthly_income),
                // Empty string / sentinel means "unassigned" — send null so backend clears it.
                assigned_to:
                    form.assigned_to && form.assigned_to !== "__unassigned"
                        ? form.assigned_to
                        : null,
            };
            if (isEdit) {
                await api.put(`/customers/${editId}`, payload);
                toast.success("Customer updated");
                nav(`/customers/${editId}`);
            } else {
                const { data } = await api.post("/customers", payload);
                toast.success("Customer created");
                nav(`/customers/${data.id}`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="space-y-5">
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => nav(-1)}
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {isEdit ? "Edit Customer" : t("add_customer")}
                </h1>
            </div>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <h3 className="font-display text-base font-bold text-slate-900">Full Name</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                        <Label>First Name<span className="text-rose-600 ml-1">*</span></Label>
                        <Input
                            data-testid="cust-first-name"
                            value={form.first_name}
                            onChange={(e) => set("first_name", UPPER(e))}
                            className="h-11 rounded-xl uppercase tracking-wide font-semibold"
                            required
                        />
                    </div>
                    <div>
                        <Label>Middle Name</Label>
                        <Input
                            data-testid="cust-middle-name"
                            value={form.middle_name}
                            onChange={(e) => set("middle_name", UPPER(e))}
                            className="h-11 rounded-xl uppercase tracking-wide font-semibold"
                        />
                    </div>
                    <div>
                        <Label>Surname<span className="text-rose-600 ml-1">*</span></Label>
                        <Input
                            data-testid="cust-surname"
                            value={form.surname}
                            onChange={(e) => set("surname", UPPER(e))}
                            className="h-11 rounded-xl uppercase tracking-wide font-semibold"
                            required
                        />
                    </div>
                </div>
                <div className="text-xs text-slate-500">
                    Will display as: <b className="text-slate-800">{[form.first_name, form.middle_name, form.surname].filter(Boolean).join(" ") || "—"}</b>
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <h3 className="font-display text-base font-bold text-slate-900">Contact & Income</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Mobile<span className="text-rose-600 ml-1">*</span></Label>
                        <Input
                            data-testid="cust-mobile"
                            value={form.mobile}
                            onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                            className="h-11 rounded-xl"
                            inputMode="tel"
                            required
                        />
                    </div>
                    <div>
                        <Label>Alt Mobile</Label>
                        <Input
                            data-testid="cust-alt-mobile"
                            value={form.alt_mobile}
                            onChange={(e) => set("alt_mobile", e.target.value.replace(/\D/g, ""))}
                            className="h-11 rounded-xl"
                            inputMode="tel"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Address<span className="text-rose-600 ml-1">*</span></Label>
                        <Textarea
                            data-testid="cust-address"
                            value={form.address}
                            onChange={(e) => set("address", UPPER(e))}
                            className="rounded-xl uppercase"
                            required
                        />
                    </div>
                    <div>
                        <Label>Occupation</Label>
                        <Input
                            data-testid="cust-occupation"
                            value={form.occupation}
                            onChange={(e) => set("occupation", UPPER(e))}
                            className="h-11 rounded-xl uppercase"
                        />
                    </div>
                    <div>
                        <Label>Monthly Income (₹)<span className="text-rose-600 ml-1">*</span></Label>
                        <AmountInput
                            data-testid="cust-income"
                            value={form.monthly_income}
                            onChange={(v) => set("monthly_income", v)}
                            className="h-11 rounded-xl"
                            required
                        />
                    </div>
                    <div>
                        <Label>Date of Birth <span className="text-slate-400 font-normal">(optional)</span></Label>
                        <Input
                            type="date"
                            data-testid="cust-dob"
                            value={form.dob || ""}
                            onChange={(e) => set("dob", e.target.value)}
                            className="h-11 rounded-xl"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                            Used to send a birthday greeting from Lokdhara.
                        </p>
                    </div>
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <UserCog className="h-4 w-4 text-blue-900" />
                    <h3 className="font-display text-base font-bold text-slate-900">
                        Assigned Employee
                        <span className="text-xs font-normal text-slate-400 ml-1">
                            (optional)
                        </span>
                    </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                        <Label>Responsible Employee</Label>
                        <Select
                            value={form.assigned_to || "__unassigned"}
                            onValueChange={(v) =>
                                set("assigned_to", v === "__unassigned" ? "" : v)
                            }
                        >
                            <SelectTrigger
                                className="h-11 rounded-xl"
                                data-testid="cust-assigned-to-select"
                            >
                                <SelectValue placeholder="— Unassigned —" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__unassigned" data-testid="cust-assigned-none">
                                    — Unassigned —
                                </SelectItem>
                                {employees.length === 0 ? (
                                    <div className="px-3 py-4 text-center text-xs text-slate-400">
                                        No active employees in your branch
                                    </div>
                                ) : employees.map((e) => (
                                    <SelectItem
                                        key={e.username}
                                        value={e.username}
                                        data-testid={`cust-assigned-opt-${e.username}`}
                                    >
                                        {e.name}{" "}
                                        <span className="text-slate-500">@{e.username}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-slate-500 mt-1.5">
                            The assigned employee will see this customer in their sheet and
                            can collect on their behalf.
                        </p>
                    </div>
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <h3 className="font-display text-base font-bold text-slate-900">
                    KYC Documents <span className="text-xs font-normal text-slate-400">(all optional)</span>
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <FileField
                        label="Customer Photo"
                        value={form.photo}
                        onChange={(v) => set("photo", v)}
                        testid="cust-photo-input"
                    />
                    <FileField
                        label="Aadhaar Image"
                        value={form.aadhaar}
                        onChange={(v) => set("aadhaar", v)}
                        testid="cust-aadhaar-input"
                    />
                    <FileField
                        label="PAN Image"
                        value={form.pan}
                        onChange={(v) => set("pan", v)}
                        testid="cust-pan-input"
                    />
                    <OtherDocsField
                        value={form.other_docs}
                        onChange={(v) => set("other_docs", v)}
                        testid="cust-other-docs-input"
                    />
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <h3 className="font-display text-base font-bold text-slate-900">
                    Witness 1 <span className="text-xs font-normal text-slate-400">(optional)</span>
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                        placeholder="Full name"
                        data-testid="cust-w1-name"
                        value={form.witness1.full_name}
                        onChange={(e) => setW("witness1", "full_name", UPPER(e))}
                        className="h-11 rounded-xl uppercase font-semibold"
                    />
                    <Input
                        placeholder="Address"
                        data-testid="cust-w1-address"
                        value={form.witness1.address}
                        onChange={(e) => setW("witness1", "address", UPPER(e))}
                        className="h-11 rounded-xl uppercase"
                    />
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <h3 className="font-display text-base font-bold text-slate-900">
                    Witness 2 <span className="text-xs font-normal text-slate-400">(optional)</span>
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                        placeholder="Full name"
                        data-testid="cust-w2-name"
                        value={form.witness2.full_name}
                        onChange={(e) => setW("witness2", "full_name", UPPER(e))}
                        className="h-11 rounded-xl uppercase font-semibold"
                    />
                    <Input
                        placeholder="Address"
                        data-testid="cust-w2-address"
                        value={form.witness2.address}
                        onChange={(e) => setW("witness2", "address", UPPER(e))}
                        className="h-11 rounded-xl uppercase"
                    />
                </div>
            </Card>

            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={busy}
                    data-testid="save-customer-btn"
                    className="h-12 rounded-xl bg-blue-900 hover:bg-blue-800 px-8"
                >
                    {busy ? "Saving…" : isEdit ? "Update Customer" : "Save Customer"}
                </Button>
            </div>
        </form>
    );
}
